import { makeAutoObservable } from "mobx";
import { identity, isError } from "lodash";
import { ApiService, InvitationRecipientRequest } from "@/api";
import { Language, languageLabels, supportedBackendLanguages } from "@/i18n/languages";
import { OptionFieldModel, RadioFieldModel, StringFieldModel } from "@/common/commonForms";
import { TMessage } from "@/i18n/tt";
import { InvitationType } from "@/types/Invitation";
import AlertMessageStore from "./AlertMessageStore";
import { emailValidator } from "@/common/common";
import { AIClient } from "@/ai/AIAssistant";
import { LocalizedTextEditorModel } from "@/text/localizedTextEditorDomain";

export type InvitationRecipient = {
  shareholderId?: number;
  companyroleId?: number;
  name: string;
  lastLogin?: string;
  email: string;
};

export class InvitationRecipientModel {
  readonly id: string;
  shareholderId?: number;
  companyroleId?: number;
  name: string;
  lastLogin?: string;
  email: StringFieldModel;

  constructor(recipient: InvitationRecipient, index: number) {
    this.id = index.toString();
    this.shareholderId = recipient.shareholderId;
    this.companyroleId = recipient.companyroleId;
    this.name = recipient.name;
    this.lastLogin = recipient.lastLogin;
    this.email = new StringFieldModel({
      value: recipient.email,
      required: true,
      validator: emailValidator,
    });
    makeAutoObservable(this, {}, { autoBind: true });
  }

  validate() {
    return this.email.validate();
  }

  get isValid() {
    return this.email.isValid;
  }

  get isMissingEmail(): boolean {
    return this.email.isEmpty || !this.email.isValid;
  }
}

export type SendInvitationDialogParams = {
  invitationType: InvitationType;
  organisationNumber: string;
  lang: Language;
  title: TMessage;
  description?: TMessage;
  adminInfo?: TMessage;
  shareholderId?: number;
  companyroleId?: number;
  roleId?: string;
  email?: string;
  customRoleName?: string;
  isShareholderPortalEnabled?: boolean;
  recipients?: InvitationRecipient[];
  infoMessage?: TMessage;
};

export type OpenSendInvitationDialogParams = SendInvitationDialogParams & {
  companyName: string | null;
};

export class SendInvitationDialogStore {
  state: "input" | "loading" | "error" = "input";
  email: StringFieldModel;
  language: RadioFieldModel<Language>;

  invitationType: InvitationType;
  apiService: ApiService;
  messageStore: AlertMessageStore;
  organisationNumber: string;
  title: TMessage;
  adminInfo?: TMessage;
  description?: TMessage;
  infoMessage?: TMessage;
  shareholderId?: number;
  companyroleId?: number;
  roleId?: string;

  error: string | null = null;
  onClose: () => void;
  isPersonalNoteEnabled: boolean = false;
  personalNote: LocalizedTextEditorModel;
  commonEmailText: TMessage;
  isShareholderPortalEnabled: boolean;
  recipients?: OptionFieldModel<InvitationRecipientModel>;

  constructor(
    params: SendInvitationDialogParams,
    apiService: ApiService,
    messageStore: AlertMessageStore,
    onClose: () => void,
    aiClient: AIClient,
    commonEmailText: TMessage,
  ) {
    this.apiService = apiService;
    this.messageStore = messageStore;
    this.onClose = onClose;
    this.infoMessage = params.infoMessage;
    this.invitationType = params.invitationType;
    this.organisationNumber = params.organisationNumber;
    this.title = params.title;
    this.adminInfo = params.adminInfo;
    this.description = params.description;
    this.shareholderId = params.shareholderId;
    this.companyroleId = params.companyroleId;
    this.roleId = params.roleId;
    this.isShareholderPortalEnabled = params.isShareholderPortalEnabled ?? true;
    this.recipients = params.recipients?.length
      ? recipientsOptionModel(params.recipients)
      : undefined;

    this.email = new StringFieldModel({
      value: params.email,
      required: true,
      validator: emailValidator,
    });
    this.language = new RadioFieldModel<Language>({
      value: supportedBackendLanguages.find((d) => d === params.lang) ?? Language.EN,
      options: supportedBackendLanguages,
      idFn: identity,
      labelFn: (l) => ({ key: languageLabels[l] }),
    });
    this.personalNote = new LocalizedTextEditorModel({
      texts: [{ text: "", language: Language.NO, translationOption: { autoTranslate: false } }],
      isReadOnly: () => false,
      label: { translated: "" },
      aiClient,
    });
    this.commonEmailText = commonEmailText;
    makeAutoObservable(this, {}, { autoBind: true });
  }

  get isMissingEmail(): boolean {
    return this.recipients
      ? this.recipients.selected.some((r) => r.isMissingEmail)
      : this.email.isEmpty || !this.email.isValid;
  }

  get selectedRecipients(): InvitationRecipientRequest[] {
    return this.recipients
      ? this.recipients.selected.map((r) => ({
          email: r.email.validatedValue,
          shareholderId: r.shareholderId,
          companyroleId: r.companyroleId,
        }))
      : [
          {
            email: this.email.validatedValue,
            shareholderId: this.shareholderId,
            companyroleId: this.companyroleId,
          },
        ];
  }

  get requiresShareholderPortal() {
    return (
      (this.invitationType === InvitationType.SHAREHOLDER ||
        this.invitationType === InvitationType.SHAREHOLDER_REPRESENTATIVE) &&
      !this.isShareholderPortalEnabled
    );
  }

  get isSubmitDisabled(): boolean {
    return (
      this.requiresShareholderPortal ||
      this.isMissingEmail ||
      (this.recipients?.isNoneSelected ?? false)
    );
  }

  setState(state: "input" | "loading" | "error") {
    this.state = state;
  }

  setError(error: unknown) {
    this.error = isError(error) ? error.message : "Unknown error";
  }

  validate(): boolean {
    if (!this.recipients) {
      return this.email.validate();
    }
    this.recipients.validate();
    this.recipients.selected.forEach((r) => r.validate());
    return this.isValid;
  }

  get isValid(): boolean {
    return this.recipients
      ? this.recipients.isValid && this.recipients.selected.every((r) => r.isValid)
      : this.email.isValid;
  }

  get closeLabel() {
    return this.state === "input" ? "invite-user.cancel" : "invite-user.close";
  }

  get showSubmitButton() {
    return this.state !== "error";
  }

  get isLoading() {
    return this.state === "loading";
  }

  togglePersonalNote() {
    this.isPersonalNoteEnabled = !this.isPersonalNoteEnabled;
    if (!this.isPersonalNoteEnabled && this.personalNote?.textModels?.length > 0) {
      this.personalNote.textModels[0].model.setAndCommitInputValue("");
    }
  }

  *submitInviteUserDialog() {
    if (!this.validate()) {
      return;
    }
    this.setState("loading");
    try {
      yield this.apiService.sendInvitation({
        invitationType: this.invitationType,
        orgNo: this.organisationNumber,
        recipients: this.selectedRecipients,
        language: this.language.value,
        roleId: this.roleId,
        personalNoteText: this.isPersonalNoteEnabled
          ? this.personalNote.textModels[0].model.validatedValue
          : undefined,
      });
      this.onClose();
      this.messageStore.showSuccess({
        key: "invite-user.success-message",
        params: { count: this.selectedRecipients.length },
      });
    } catch (e) {
      this.setState("error");
      this.setError(e);
    }
  }
}

export const recipientsOptionModel = (
  recipients: InvitationRecipient[],
): OptionFieldModel<InvitationRecipientModel> => {
  const models = recipients.map((r, index) => new InvitationRecipientModel(r, index));
  return new OptionFieldModel<InvitationRecipientModel>({
    options: models,
    required: true,
    idFn: (r) => r.id,
    labelFn: (r) => ({ translated: r.name }),
    value: models.filter((r) => r.lastLogin === undefined),
  });
};
